import {
  ComponentProps,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  faPenToSquare,
  faTrashXmark,
} from '@fortawesome/pro-regular-svg-icons';

import { AdminSection, TenantRole } from '@typings';
import { removeSystemUserRole, updateSystemUserRole } from '@services';
import { userSelector } from '@selectors';
import { useSelector } from '@hooks';
import { capitalize, toastifyResponseError } from '@utils';

import { Button, Field, Icon, Modal, ModalRef } from '@components';

const schema = z.object({
  role: z.nativeEnum(AdminSection.Role),
});

type Schema = z.infer<typeof schema>;

type Props = {
  user: AdminSection.User;
  onRemoveRole?: (name: AdminSection.User['name']) => void;
  onUpdateRole?: (
    name: AdminSection.User['name'],
    role?: AdminSection.Role,
  ) => void;
};

export const AdminSectionUserActions = memo<Props>(
  ({ user, onUpdateRole, onRemoveRole }) => {
    const { systemRole } = useSelector(userSelector);

    const editModalRef = useRef<ModalRef>(null);
    const removeModalRef = useRef<ModalRef>(null);

    const [isRemoving, setRemoving] = useState(false);

    const {
      register,
      handleSubmit,
      formState: { isSubmitting: isEditing },
    } = useForm<Schema>({
      resolver: zodResolver(schema),
      defaultValues: { role: user.role },
    });

    const roles = useMemo(
      () =>
        Object.values(TenantRole).reduce<
          Exclude<ComponentProps<typeof Field.Select>['options'], undefined>
        >((acc, role) => {
          if (
            systemRole === AdminSection.Role.Admin ||
            role !== TenantRole.Admin
          ) {
            acc.push({
              value: role,
              text: capitalize(role),
            });
          }

          return acc;
        }, []),
      [systemRole],
    );

    const handleEdit = handleSubmit(async ({ role }) => {
      if (user.role === role) {
        editModalRef.current?.close();
        return;
      }

      try {
        await updateSystemUserRole({ name: user.name, role });

        onUpdateRole?.(user.name, role);
        editModalRef.current?.close();
      } catch (error) {
        toastifyResponseError(error);
      }
    });

    const handleRemove = useCallback(async () => {
      setRemoving(true);

      try {
        await removeSystemUserRole({ name: user.name });

        onRemoveRole?.(user.name);
        removeModalRef.current?.close();
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        setRemoving(false);
      }
    }, [onRemoveRole, user.name]);

    const editModalContent = (
      <Modal.Content title={`Edit User ${user.name}`} className="w-[480px]">
        <form className="flex flex-col gap-6" onSubmit={handleEdit}>
          <Field.Select
            {...register('role')}
            label="Role"
            options={roles}
            disabled={isEditing}
          />
        </form>
        <Modal.Footer>
          <Button loading={isEditing} className="px-14" onClick={handleEdit}>
            Save
          </Button>
        </Modal.Footer>
      </Modal.Content>
    );

    const removeModalContent = (
      <Modal.Content title="Delete User Role" className="w-[520px]">
        <p className="flex flex-wrap items-center justify-center gap-1">
          Are you sure you want to delete role <u>{user.role}</u> for
          <b>
            {user.name}
            <span className="font-normal">?</span>
          </b>
        </p>

        <Modal.Footer>
          <Button variant="secondary" onClick={removeModalRef.current?.close}>
            Cancel
          </Button>
          <Button
            variant="error"
            loading={isRemoving}
            className="px-14"
            onClick={handleRemove}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal.Content>
    );

    return (
      <>
        <Modal ref={editModalRef} content={editModalContent}>
          <Button
            theme
            className="text-neural-04 transition-all will-change-transform group-hover:text-black"
          >
            <Icon icon={faPenToSquare} />
          </Button>
        </Modal>

        <span className="h-4 border-l" />

        <Modal ref={removeModalRef} content={removeModalContent}>
          <Button
            theme
            disabled={!user.role}
            className="text-neural-04 transition-all will-change-transform group-hover:text-error"
          >
            <Icon icon={faTrashXmark} />
          </Button>
        </Modal>
      </>
    );
  },
);

AdminSectionUserActions.displayName = 'AdminSectionUserActions';
